import React from 'react';

import { useTranslation } from 'react-i18next';

import { ICON_SET_FONTAWESOME } from 'constants-activate';
import { getDisplayName } from 'lane-shared/helpers';
import { PropertyTenantChannel } from 'react-hooks';

import { Button, H4, Icon, Modal, P } from 'design-system-web';
import { ChannelAvatar } from '../ChannelAvatar';

import { isPropertyChannel } from './utils';

import styles from './HomeChannelPickerModal.scss';

type ConfirmationModalProps = {
  isOpen: boolean;
  selectedChannel: PropertyTenantChannel | undefined;
  isSaving: boolean;
  saveError: string | null;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
};

export function ConfirmationModal({
  isOpen,
  selectedChannel,
  isSaving,
  saveError,
  onCancel,
  onConfirm,
}: ConfirmationModalProps) {
  const { t } = useTranslation();

  return (
    <Modal
      className={styles.confirmModal}
      isOpen={isOpen}
      onClose={onCancel}
      title={t('web.userProfile.homeChannel.confirmTitle')}
      testId="homeChannelConfirmationModal"
      includeHeader={false}
      isCloseButtonHidden
      disableContentPadding
    >
      <div className={styles.confirmModalContainer}>
        <H4 className={styles.confirmTitle}>
          {t('web.userProfile.homeChannel.confirmTitle')}
        </H4>
        <div className={styles.confirmDescriptionContainer}>
          <P className={styles.confirmDescription}>
            {t('web.userProfile.homeChannel.confirmDescriptionPrefix')}{' '}
            <strong>
              {selectedChannel ? getDisplayName(selectedChannel) : ''}
            </strong>{' '}
            {t('web.userProfile.homeChannel.confirmDescriptionSuffix')}
          </P>
        </div>
        {selectedChannel && (
          <div className={styles.confirmChannelCard}>
            <ChannelAvatar
              name={getDisplayName(selectedChannel)}
              logo={selectedChannel.profile?.logo}
            />
            <div className={styles.confirmChannelInfo}>
              <P className={styles.confirmChannelName}>
                {getDisplayName(selectedChannel)}
              </P>
              <P className={styles.confirmChannelType}>
                {isPropertyChannel(selectedChannel)
                  ? t('web.userProfile.homeChannel.property')
                  : t('web.userProfile.homeChannel.tenant')}
              </P>
            </div>
            <div className={styles.confirmHomeIcon}>
              <Icon name="home" set={ICON_SET_FONTAWESOME} type="far" />
            </div>
          </div>
        )}

        {saveError && <P className={styles.saveError}>{saveError}</P>}

        <div className={styles.confirmModalFooter}>
          <Button
            className={styles.confirmCancelBtn}
            variant="secondary"
            onClick={onCancel}
            disabled={isSaving}
            testId="cancelHomeChannelConfirmation"
          >
            {t('web.userProfile.homeChannel.cancel')}
          </Button>
          <Button
            className={styles.confirmSubmitBtn}
            variant="primary"
            onClick={onConfirm}
            disabled={isSaving}
            loading={isSaving}
            testId="confirmHomeChannelSubmit"
          >
            {t('web.userProfile.homeChannel.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
